import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "@gravity-ui/icons";
import type { Event } from "../../reducers/apiTypes";
import { EventCard } from "./EventCard";

type Props = {
    events: Event[];
    onSelectEvent?: (event: Event) => void;
};

export function MapEventPopup({ events, onSelectEvent }: Props) {
    const [index, setIndex] = useState(0);
    const count = events.length;
    const eventSignature = useMemo(
        () => events.map((e) => e.id).join("-"),
        [events]
    );

    useEffect(() => {
        setIndex(0);
    }, [eventSignature]);

    const go = useCallback(
        (delta: number) => {
            if (count <= 1) return;
            setIndex((i) => (i + delta + count) % count);
        },
        [count]
    );

    if (count === 0) {
        return (
            <div className="rounded-2xl bg-white px-4 py-3 text-xs text-muted">
                No events
            </div>
        );
    }

    return (
        <div className="map-event-popup">
            <div
                onClick={(e) => {
                    e.stopPropagation();
                    onSelectEvent?.(events[index]);
                }}
            >
                <EventCard event={events[index]} />
            </div>

            {count > 1 && (
                <div className="mt-1 flex items-center justify-between gap-2 rounded-b-2xl bg-white px-2 py-1.5">
                    <button
                        type="button"
                        onClick={() => go(-1)}
                        className="inline-flex size-8 items-center justify-center rounded-full bg-surface-secondary text-foreground transition hover:bg-surface-tertiary"
                        aria-label="Previous event"
                    >
                        <ChevronLeft className="size-4" />
                    </button>
                    <div className="flex gap-1">
                        {events.map((event, i) => (
                            <button
                                key={event.id}
                                type="button"
                                onClick={() => setIndex(i)}
                                className={`size-1.5 rounded-full transition ${
                                    i === index
                                        ? "bg-accent-orange"
                                        : "bg-border"
                                }`}
                                aria-label={`Go to event ${i + 1} of ${count}`}
                            />
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => go(1)}
                        className="inline-flex size-8 items-center justify-center rounded-full bg-surface-secondary text-foreground transition hover:bg-surface-tertiary"
                        aria-label="Next event"
                    >
                        <ChevronRight className="size-4" />
                    </button>
                </div>
            )}
        </div>
    );
}
